import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { DateFormulaService } from '../../../../../core/services/proprietary/date-formula.service';
import { DateFormulaType } from '../../../../../core/services/proprietary/date-formula.types';

const noop = () => { };

@Component({
    standalone: false,
    selector: 'ep-date-or-date-formula-input',
    template: `
    <div class="unity-form-group date" style="margin:0px; padding:0px">
      <label *ngIf="!noLabel"><ng-content></ng-content></label>
      <div #dpGroup class="unity-form-row" style="position:relative;">
        <input
          class="unity-date"
          #inputElement
          [(ngModel)]="currentInput"
          (ngModelChange)="inputChanged($event)"
          [class]="!disabled ? 'form-control' : ''"
          [disabled]="disabled"
          (focus)="onFocus()"
          (blur)="onBlur()"
        />
        <aurora-icon
          icon="calculator"
          #toggleButton
          (click)="disabled ? null : openPopup()"
          tabindex="-1"
          [style.cursor]="disabled ? 'not-allowed' : ''"
        ></aurora-icon>
        <ng-container *ngIf="!isDateFormula && isCalendarOpen">
          <ngb-datepicker
            *ngIf="isCalendarOpen"
            [(ngModel)]="currentDate"
            [startDate]="currentDate"
            firstDayOfWeek="7"
            style="position:absolute;z-index:3;background-color:#ffffff;top: var(--height-input);"
            (click)="$event.preventDefault(); $event.stopPropagation()"
            (dateSelect)="datePickerChanged($event)"
          >
          </ngb-datepicker>
        </ng-container>
      </div>
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: DateOrDateFormulaInputComponent, multi: true }]
})
export class DateOrDateFormulaInputComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
    @ViewChild('epInput') epInput;

    @ViewChild('dpGroup', { static: true })
    dpGroup: ElementRef;

    @ViewChild('toggleButton', { static: true })
    toggleButton: ElementRef;

    @Input() noLabel: boolean = false;
    @Input() format: DateFormulaType = DateFormulaType.Date;

    @Input()
    disabled: boolean = false;

    @Output()
    blur = new EventEmitter();

    @Output()
    focus = new EventEmitter();

    public currentInput: string;
    public currentDate: NgbDateStruct;

    calculatedDate: string;

    private innerValue: any = '';

    isDateFormula: boolean = true;
    isCalendarOpen: boolean = false;
    displayFormat = 'MM/DD/YYYY';
    private dataFormat = 'YYYY-MM-DDTHH:mm:ss-HH:mm';

    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    unlistenCallback: any;

    constructor(private dateFormula: DateFormulaService, private cdRef: ChangeDetectorRef, private renderer: Renderer2) { }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: any) {
        if (this.isEscapeKey(event)) {
            if (this.isCalendarOpen) {
                this.isCalendarOpen = false;
            }
            event.preventDefault();
            event.stopPropagation();
        } else if (this.isTabOrShiftTabKey(event)) {
            this.isCalendarOpen = false;
        }
    }

    isEscapeKey(event: KeyboardEvent): boolean {
        return event.key == 'Escape' || event.key == 'Esc';
    }

    isTabOrShiftTabKey(event: KeyboardEvent): boolean {
        return event.key == 'Tab' || (event.shiftKey && event.key == 'Tab');
    }

    ngAfterViewInit() {
        this.unlistenCallback = this.renderer.listen('window', 'click', (e: Event) => {
            if (!this.toggleButton.nativeElement.contains(e.target) && !this.dpGroup.nativeElement.contains(e.target)) {
                this.isCalendarOpen = false;
                this.cdRef.markForCheck();
            }
        });
    }

    ngOnDestroy() {
        this.unlistenCallback();
    }

    inputChanged(inputVal: string) {
        let newVal = '';
        if (inputVal) {
            newVal = inputVal;
        }
        if (this.usesDateFormula(inputVal)) {
            this.isDateFormula = true;
            this.value = newVal;
        } else {
            this.isDateFormula = false;
            let m = moment.parseZone(newVal, this.displayFormat);
            if (m && m.isValid()) {
                this.value = m.format(this.dataFormat);
                this.currentDate = { year: m.year(), month: m.month() + 1, day: m.date() };
            } else {
                this.value = '';
                this.currentDate = null;
            }
        }
    }

    usesDateFormula(value: string): boolean {
        if (!value) {
            return true; //if the value is blank or cleared, the date formula option should become available
        }

        return this.dateFormula.validateFormula(this.format, value);
    }

    public datePickerChanged(dateVal: NgbDateStruct) {
        let m = moment.parseZone({ year: dateVal.year, month: dateVal.month - 1, day: dateVal.day });

        if (m && m.isValid()) {
            this.isDateFormula = false;
            this.value = m.format(this.dataFormat);
            this.currentInput = m.format(this.displayFormat);
        } else {
            this.value = '';
            this.currentDate = null;
        }

        if (this.isCalendarOpen) {
            this.isCalendarOpen = false;
        }
        this.onTouchedCallback();
        this.blur.emit();
    }

    get value(): any {
        return this.innerValue;
    }

    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    onBlur() {
        if (this.innerValue) {
            if (!this.dateFormula.validateFormula(this.format, this.innerValue)) {
                this.isDateFormula = false;
                let m = moment.parseZone(this.currentInput, this.displayFormat);
                if (m && m.isValid()) {
                    this.value = m.format(this.dataFormat);
                    this.currentInput = m.format(this.displayFormat);
                } else {
                    this.value = '';
                    this.currentInput = '';
                }
            } else {
                this.isDateFormula = true;
            }
        }

        this.blur.emit();
        this.onTouchedCallback();
    }

    onFocus() {
        this.focus.emit();
    }

    writeValue(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            if (this.usesDateFormula(value)) {
                this.currentInput = value;
            } else {
                let m = moment.parseZone(value);
                if (value && value > '' && m && m.isValid()) {
                    this.currentInput = m.format(this.displayFormat);
                    this.currentDate = { year: m.year(), month: m.month() + 1, day: m.date() };
                } else {
                    this.currentDate = null;
                    this.currentInput = null;
                }
            }
            this.cdRef.detectChanges();
        }
    }

    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    openPopup() {
        if (this.isDateFormula) {
            this.openDateFormulaDialog(this.format);
        } else {
            this.isCalendarOpen = true;
        }
    }

    openDateFormulaDialog(formulaType: DateFormulaType) {
        this.dateFormula.openDialog(formulaType, this.dialogResult.bind(this));
    }

    dialogResult(result: any) {
        if (result) {
            this.value = result;
            this.currentInput = result;
            this.onBlur();
        }
    }
}
